import { createContext, Dispatch } from 'react'
import { ActionRequest, DomainIntegrationsManagerContextState } from './context'
import { DomainDto } from '../../dtos/domain'
import { Domain } from '@pushly/models/lib/structs/domain'
import {
    CreateBlueConicDomainIntegrationRequest,
    CreateBlueConicIntegrationRequest,
    CreateDomainIntegrationRequest,
    GetDomainIntegrationResponse,
    isGetBlueConicDomainIntegrationResponse,
    isGetLyticsDomainIntegrationResponse,
    isGetShopifyDomainIntegrationResponse,
    isGetSlackDomainIntegrationResponse,
    isGetWordpressDomainIntegrationResponse,
    UpdateBlueConicDomainIntegrationRequest,
} from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'

type DomainIntegrationsManagerDispatchPack =
    | { type: 'set-domain'; domain: DomainDto | Domain }
    | { type: 'set-integrations'; integrations: GetDomainIntegrationResponse[] }
    | { type: 'set-integration'; integration: GetDomainIntegrationResponse }
    | { type: 'rem-integration'; integration: GetDomainIntegrationResponse }
    | { type: 'add-integration'; integration: GetDomainIntegrationResponse }
    | { type: 'request-integrations-reload' }
    // Action Requests
    | { type: 'request-integration-add'; provider: DomainIntegrationProviders }
    | {
          type: 'request-integration-edit'
          provider: DomainIntegrationProviders
          integration: GetDomainIntegrationResponse
      }
    | {
          type: 'request-integration-rem'
          provider: DomainIntegrationProviders
          integration: GetDomainIntegrationResponse
      }
    | { type: 'set-action-request-processing' }
    | { type: 'clear-action-request' }

function isLoading(state: DomainIntegrationsManagerContextState): boolean {
    return !state.domain || !state.integrations || Boolean(state.integrations_reload_requested)
}

export function domainIntegrationsManagerReducer(
    state: DomainIntegrationsManagerContextState,
    action: DomainIntegrationsManagerDispatchPack,
): DomainIntegrationsManagerContextState {
    const nextState = { ...state }

    switch (action.type) {
        case 'set-domain': {
            nextState.domain = action.domain
            nextState.loading = isLoading(nextState)
            break
        }
        case 'set-integrations': {
            nextState.integrations = action.integrations
            nextState.integrations_reload_requested = undefined
            nextState.loading = isLoading(nextState)
            break
        }
        case 'add-integration': {
            nextState.integrations = [...(state.integrations ?? []), action.integration]
            break
        }
        case 'set-integration': {
            const nextIntegrations = Array.from(state.integrations ?? [])
            const swapIdx = nextIntegrations.findIndex((di) => di.id === action.integration.id)
            if (swapIdx >= 0) {
                nextIntegrations.splice(swapIdx, 1, action.integration)
            }
            nextState.integrations = nextIntegrations
            break
        }
        case 'rem-integration': {
            const nextIntegrations = Array.from(state.integrations ?? [])
            const remIdx = nextIntegrations.findIndex((di) => di.id === action.integration.id)
            if (remIdx >= 0) {
                nextIntegrations.splice(remIdx, 1)
            }
            nextState.integrations = nextIntegrations
            break
        }
        case 'request-integrations-reload': {
            setRequestIntegrationsReloadState(nextState)
            break
        }
        case 'request-integration-add': {
            const baseAddActionRequest: Pick<ActionRequest<any, any>, 'type'> = {
                type: 'add',
            }

            const baseCreateRequestProperties: Pick<CreateDomainIntegrationRequest, 'domain_id' | 'is_active'> = {
                domain_id: nextState.domain!.id,
                is_active: true,
            }

            switch (action.provider) {
                case DomainIntegrationProviders.BLUECONIC_REC: {
                    nextState.actionRequest = {
                        ...baseAddActionRequest,
                        provider: action.provider,
                        data: CreateBlueConicDomainIntegrationRequest.from({
                            ...baseCreateRequestProperties,
                            provider: action.provider,
                            data: new CreateBlueConicIntegrationRequest(),
                        }),
                    }
                }
            }

            break
        }
        case 'request-integration-edit': {
            const baseEditActionRequest: Pick<ActionRequest<any, any>, 'type'> = {
                type: 'edit',
            }

            switch (action.provider) {
                case DomainIntegrationProviders.BLUECONIC_REC: {
                    if (isGetBlueConicDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseEditActionRequest,
                            provider: action.provider,
                            data: UpdateBlueConicDomainIntegrationRequest.parseJsonObject(
                                action.integration.serialize(),
                            ),
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.LYTICS: {
                    if (isGetLyticsDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseEditActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.SHOPIFY: {
                    if (isGetShopifyDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseEditActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.SLACK: {
                    if (isGetSlackDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseEditActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.WORDPRESS: {
                    if (isGetWordpressDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseEditActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
            }
            break
        }
        case 'request-integration-rem': {
            const baseRemActionRequest: Pick<ActionRequest<any, any>, 'type'> = {
                type: 'rem',
            }

            switch (action.provider) {
                case DomainIntegrationProviders.BLUECONIC_REC: {
                    if (isGetBlueConicDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseRemActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.LYTICS: {
                    if (isGetLyticsDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseRemActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.SHOPIFY: {
                    if (isGetShopifyDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseRemActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.SLACK: {
                    if (isGetSlackDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseRemActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
                case DomainIntegrationProviders.WORDPRESS: {
                    if (isGetWordpressDomainIntegrationResponse(action.integration)) {
                        nextState.actionRequest = {
                            ...baseRemActionRequest,
                            provider: action.provider,
                            data: action.integration,
                        }
                    }
                    break
                }
            }
            break
        }
        case 'set-action-request-processing': {
            if (!nextState.actionRequest) break
            nextState.actionRequest.processing = true
            break
        }
        case 'clear-action-request': {
            nextState.actionRequest = undefined
            break
        }
    }

    return nextState
}

/**
 * @mutating - Mutates state to `integrations_reload_requested=true` and `loading=true`
 */
function setRequestIntegrationsReloadState(state: DomainIntegrationsManagerContextState): void {
    state.integrations_reload_requested = true
}

export const DomainIntegrationsManagerDispatchContext = createContext<Dispatch<DomainIntegrationsManagerDispatchPack>>(
    null!,
)

/* webpack-strip-block:removed */
